import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Form, InputGroup, Card, Modal, Row, Col, Spinner, Badge } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus, faBoxOpen, faEdit, faTrash, faSync } from '@fortawesome/free-solid-svg-icons';
import MainLayout from '../../layouts/MainLayout';

// Aktifkan mode debug untuk membantu troubleshooting
const DEBUG = true;

const logDebug = (...args) => {
  if (DEBUG) {
    console.log('[DEBUG]', ...args);
  }
};

// Fungsi untuk memformat angka menjadi Rupiah
const formatRupiah = (value) => {
  if (value === undefined || value === null || value === '') {
    return 'Rp 0';
  }
  
  let numValue;
  
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    numValue = cleaned ? parseFloat(cleaned) : 0;
  } else {
    numValue = Number(value);
  }
  
  if (isNaN(numValue)) {
    console.warn('Invalid number format for value:', value);
    return 'Rp 0';
  }
  
  try {
    return `Rp ${numValue.toLocaleString('id-ID')}`;
  } catch (err) {
    console.error('Error formatting number:', err);
    return `Rp ${numValue}`;
  }
};

const StockProdukJadi = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    sku: '',
    category: 'finished_product',
    current_stock: '0',
    minimum_stock: '0',
    price: '0',
    location: '',
    notes: ''
  });

  // Tambahkan state untuk loading submit
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    // Ubah formData ketika editProduct berubah
    if (editProduct) {
      setFormData({
        name: editProduct.name || '',
        code: editProduct.code || '',
        sku: editProduct.sku || '',
        category: editProduct.category || 'finished_product',
        current_stock: String(editProduct.current_stock || 0),
        minimum_stock: String(editProduct.minimum_stock || 0),
        price: String(editProduct.price || 0),
        location: editProduct.location || '',
        notes: editProduct.notes || ''
      });
    }
  }, [editProduct]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        window.location.href = '/login';
        return;
      }

      // Gunakan endpoint yang benar dan tambahkan filter untuk produk jadi
      const response = await axios.get('https://rumahakrilik.id/api/inventory/?category=finished_product', {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });

      logDebug('Raw API response:', response.data);

      // Proses data sesuai format dari backend
      let productsList = [];
      if (Array.isArray(response.data)) {
        productsList = response.data;
      } else if (response.data.results && Array.isArray(response.data.results)) {
        productsList = response.data.results;
      } else {
        toast.warning('Format data tidak sesuai harapan');
      }

      // Standardisasi format data
      const standardizedProducts = productsList.map(product => ({
        id: product.id,
        name: product.name || 'Tidak ada nama',
        code: product.code || product.sku || '-',
        sku: product.sku || product.code || '-',
        category: product.category || 'finished_product',
        current_stock: product.current_stock !== undefined ? product.current_stock : 0,
        minimum_stock: product.minimum_stock !== undefined ? product.minimum_stock : 0,
        price: product.price !== undefined ? product.price : 0,
        location: product.location || '-',
        notes: product.notes || '-'
      }));

      logDebug('Processed products:', standardizedProducts);
      setProducts(standardizedProducts);
    } catch (error) {
      console.error('Error fetching finished products:', error);
      
      if (error.response) {
        logDebug('Error response:', error.response);
        toast.error(`Gagal memuat data: ${error.response.data?.message || error.response.statusText}`);
      } else {
        toast.error('Gagal memuat data produk jadi: ' + error.message);
      }
      
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    fetchProducts();
  };

  const validateForm = () => {
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Nama produk harus diisi');
      return false;
    }
    
    if (!formData.category) {
      toast.error('Kategori harus dipilih');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        window.location.href = '/login';
        return;
      }

      // Siapkan data untuk dikirim
      const productData = {
        name: formData.name.trim(),
        sku: formData.sku || `PROD-${Date.now().toString().slice(-6)}`,
        category: 'finished_product',
        current_stock: parseInt(formData.current_stock, 10) || 0,
        minimum_stock: parseInt(formData.minimum_stock, 10) || 0,
        price: parseInt(formData.price.replace(/[^\d]/g, ''), 10) || 0,
        location: formData.location.trim(),
        notes: formData.notes.trim()
      };

      logDebug('Submitting product data:', productData);
      
      if (editProduct) {
        // Update produk yang sudah ada
        await axios.put(`https://rumahakrilik.id/api/inventory/${editProduct.id}/`, productData, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        toast.success('Produk berhasil diperbarui!');
      } else {
        // Buat produk baru
        await axios.post('https://rumahakrilik.id/api/inventory/', productData, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        toast.success('Produk berhasil ditambahkan!');
      }
      
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      
      if (error.response) {
        logDebug('Error response:', error.response);
        toast.error(`Gagal ${editProduct ? 'memperbarui' : 'menambahkan'} produk: ${error.response.data?.message || error.response.statusText}`);
      } else {
        toast.error(`Gagal ${editProduct ? 'memperbarui' : 'menambahkan'} produk: ${error.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (productId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      // Fetch detail produk untuk mendapatkan data terbaru
      const response = await axios.get(`https://rumahakrilik.id/api/inventory/${productId}/`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });

      logDebug('Edit product data:', response.data);
      
      setEditProduct(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching product details:', error);
      toast.error('Gagal memuat detail produk');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus produk ini?')) {
      try {
        const token = localStorage.getItem('jwtToken');
        
        await axios.delete(`https://rumahakrilik.id/api/inventory/${id}/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Produk berhasil dihapus!');
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Gagal menghapus produk');
      }
    }
  };

  const resetForm = () => {
    setEditProduct(null);
    setFormData({
      name: '',
      code: '',
      sku: '',
      category: 'finished_product',
      current_stock: '0',
      minimum_stock: '0',
      price: '0',
      location: '',
      notes: ''
    });
  };

  const getStockStatus = (current, minimum) => {
    if (current <= 0) {
      return <Badge bg="danger">Kosong</Badge>;
    } else if (current <= minimum) {
      return <Badge bg="warning">Stok Rendah</Badge>;
    } else {
      return <Badge bg="success">Tersedia</Badge>;
    }
  };

  const filteredProducts = products.filter(product =>
    product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <MainLayout>
      <Container fluid className="py-4">
        <h1 className="mb-4">Stock Produk Jadi</h1>
        
        <div className="d-flex justify-content-between mb-3">
          <InputGroup style={{ maxWidth: '400px' }}>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Cari berdasarkan nama, kode, SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          
          <div>
            <Button 
              variant="outline-secondary" 
              className="me-2" 
              onClick={refreshData}
              disabled={loading}
            >
              {loading ? (
                <><Spinner animation="border" size="sm" className="me-1" /> Memuat...</>
              ) : (
                <><FontAwesomeIcon icon={faSync} className="me-1" /> Refresh Data</>
              )}
            </Button>
            <Button 
              variant="primary" 
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" /> Tambah Produk
            </Button>
          </div>
        </div>

        <Card>
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Memuat data produk jadi...</p>
              </div>
            ) : (
              <>
                {filteredProducts.length === 0 ? (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faBoxOpen} size="3x" className="text-muted mb-3" />
                    <p>Tidak ada data produk jadi</p>
                    <Button variant="outline-primary" onClick={() => {
                      resetForm();
                      setShowModal(true);
                    }}>
                      Tambah Produk Baru
                    </Button>
                  </div>
                ) : (
                  <Table responsive striped bordered hover>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Nama Produk</th>
                        <th>Kategori</th>
                        <th>Stok</th>
                        <th>Min. Stok</th>
                        <th>Harga</th>
                        <th>Lokasi</th>
                        <th>Status</th>
                        <th>Catatan</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => (
                        <tr key={product.id}>
                          <td>{product.sku || '-'}</td>
                          <td>{product.name}</td>
                          <td>Produk Jadi</td>
                          <td>{product.current_stock}</td>
                          <td>{product.minimum_stock}</td>
                          <td>{formatRupiah(product.price)}</td>
                          <td>{product.location}</td>
                          <td>
                            {getStockStatus(product.current_stock, product.minimum_stock)}
                          </td>
                          <td>{product.notes}</td>
                          <td>
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              className="me-1" 
                              onClick={() => handleEdit(product.id)}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              onClick={() => handleDelete(product.id)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        {/* Modal for Add/Edit Product */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editProduct ? 'Edit Produk' : 'Tambah Produk Baru'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nama Produk</Form.Label>
                    <Form.Control 
                      type="text" 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>SKU</Form.Label>
                    <Form.Control 
                      type="text"
                      placeholder="Akan dibuat otomatis jika kosong"
                      value={formData.sku}
                      onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Kategori</Form.Label>
                    <Form.Control
                      type="text"
                      value="Produk Jadi"
                      disabled
                    />
                    <Form.Text className="text-muted">
                      Kategori tetap sebagai Produk Jadi
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Lokasi</Form.Label>
                    <Form.Control 
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Jumlah Stok</Form.Label>
                    <Form.Control 
                      type="number"
                      min="0"
                      required
                      value={formData.current_stock}
                      onChange={(e) => setFormData({...formData, current_stock: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Minimum Stok</Form.Label>
                    <Form.Control 
                      type="number"
                      min="0"
                      value={formData.minimum_stock}
                      onChange={(e) => setFormData({...formData, minimum_stock: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Harga (Rp)</Form.Label>
                    <Form.Control 
                      type="text"
                      required
                      value={formData.price}
                      onChange={(e) => {
                        const value = e.target.value.replace(/^0+|[^\d]/g, '');
                        setFormData({...formData, price: value});
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Catatan</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </Form.Group>
              
              <div className="d-flex justify-content-end">
                <Button 
                  variant="secondary" 
                  className="me-2" 
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                >
                  Batal
                </Button>
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <><Spinner animation="border" size="sm" className="me-1" /> Menyimpan...</>
                  ) : (
                    editProduct ? 'Perbarui' : 'Simpan'
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </Container>
    </MainLayout>
  );
};

export default StockProdukJadi;